import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, CheckCircle2, Circle, Star, Gift, Info,
  ChevronDown, ChevronUp, Sparkles, Loader2, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { MONTHS_DATA } from '../../data/curriculum';
import useAuthStore from '../../store/authStore';
import progressService from '../../services/progressService';

// Helper function to find week data from curriculum (fallback)
const getWeekDataFromCurriculum = (weekNumber) => {
  const num = parseInt(weekNumber, 10);
  for (const month of MONTHS_DATA) {
    const week = month.weeks.find((w) => w.number === num);
    if (week) {
      return {
        ...week,
        month: month.name,
        points: 100,
        description: getWeekDescription(week),
        activities: week.activities.map((a) => ({
          ...a,
          instructions: getActivityInstructions(a),
          tips: getActivityTips(a),
          is_completed: false,
        })),
      };
    }
  }
  return null;
};

// Helper functions for demo content
const getWeekDescription = (week) => {
  const descriptions = {
    1: 'Start your plant journey! Collect seeds, name your plant, and begin your growth journal.',
    2: 'Learn how to care for your plant daily with proper watering and sunlight.',
    3: 'Watch for the first signs of your seed sprouting! This week is all about patience and observation.',
    4: 'Establish ownership of your plant and celebrate Clean Energy Day!',
  };
  return descriptions[week.number] || `Explore the theme of ${week.theme} through hands-on activities.`;
};

const getActivityInstructions = (activity) => {
  return activity.instructions || `Complete the "${activity.title}" activity by following the steps your teacher provides. Remember to document your progress!`;
};

const getActivityTips = (activity) => {
  if (activity.tips) return activity.tips;
  const tips = [
    'Take your time and enjoy the process!',
    'Ask a classmate for help if needed.',
    'Document everything in your journal.',
    'Share your discoveries with others!',
  ];
  return tips[Math.floor(Math.random() * tips.length)];
};

export default function WeekDetail() {
  const { weekNumber } = useParams();
  const navigate = useNavigate();
  const { student, isAuthenticated, refreshStudentFromResponse } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weekData, setWeekData] = useState(null);
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [localCompletions, setLocalCompletions] = useState({});

  // Fetch week data from API
  useEffect(() => {
    const fetchWeekData = async () => {
      try {
        setLoading(true);

        // Try to fetch from API if authenticated
        if (isAuthenticated && student) {
          try {
            const data = await progressService.getWeek(parseInt(weekNumber, 10));
            setWeekData({
              ...data,
              week_number: data.number,
              // Find month info from curriculum for display
              month: getMonthName(data.number),
            });
            setError(null);
            setLoading(false);
            return;
          } catch (apiErr) {
            console.error('API fetch failed, using fallback:', apiErr);
            setError('Using offline data');
          }
        }

        // Fallback to curriculum data
        const fallbackData = getWeekDataFromCurriculum(weekNumber);
        setWeekData(fallbackData);
      } catch (err) {
        console.error('Failed to load week:', err);
        setError('Failed to load week data');
      } finally {
        setLoading(false);
      }
    };

    fetchWeekData();
  }, [weekNumber, isAuthenticated, student]);

  // Helper to get month name from week number
  const getMonthName = (weekNum) => {
    for (const month of MONTHS_DATA) {
      if (month.weeks.some(w => w.number === weekNum)) {
        return month.name;
      }
    }
    return '';
  };

  // Merge API activities with local completions
  const activities = useMemo(() => {
    if (!weekData?.activities) return [];

    return weekData.activities.map((a) => ({
      ...a,
      id: a.id || a.id,
      title: a.title,
      description: a.description,
      instructions: a.instructions || getActivityInstructions(a),
      tips: a.tips || getActivityTips(a),
      points: a.points_value || a.points || 25,
      completed: localCompletions[a.id] ?? a.is_completed ?? a.completed ?? false,
    }));
  }, [weekData, localCompletions]);

  const week = weekData ? {
    ...weekData,
    number: weekData.week_number || weekData.number,
    title: weekData.title,
    theme: weekData.theme,
    plantCareFocus: weekData.plant_care_focus || weekData.plantCareFocus,
    points: weekData.points_value || weekData.points || 100,
    description: weekData.description || getWeekDescription(weekData),
    envDay: weekData.environmental_days?.[0] || weekData.envDay,
    activities,
  } : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-leaf-500 mx-auto mb-2" />
          <p className="text-gray-500">Loading week activities...</p>
        </div>
      </div>
    );
  }

  if (!week) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Week not found</p>
        <button
          onClick={() => navigate('/app')}
          className="mt-4 btn-primary"
        >
          Back to Home
        </button>
      </div>
    );
  }

  const completedCount = activities.filter((a) => a.completed).length;
  const totalCount = activities.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const handleComplete = async (activityId) => {
    setCompletingId(activityId);

    try {
      // Try API call if authenticated
      if (isAuthenticated && student) {
        try {
          const result = await progressService.completeActivity(activityId);
          toast.success(`Activity completed! +${result.points_earned || 25} points`);

          // Update auth store with new points/streak
          refreshStudentFromResponse(result);

          // Update local state
          setLocalCompletions((prev) => ({
            ...prev,
            [activityId]: true,
          }));

          setCompletingId(null);
          setExpandedActivity(null);
          return;
        } catch (apiErr) {
          console.error('API complete failed:', apiErr);
          // Show error but still update locally for demo
          if (apiErr.response?.status !== 400) {
            toast.error('Could not save to server, but marked locally');
          } else {
            // Already completed
            toast.success('Activity already completed!');
          }
        }
      } else {
        // Simulate delay for demo mode
        await new Promise((resolve) => setTimeout(resolve, 800));
        toast.success('Activity completed! +25 points');
      }

      // Update local state
      setLocalCompletions((prev) => ({
        ...prev,
        [activityId]: true,
      }));
    } catch (err) {
      console.error('Error completing activity:', err);
      toast.error('Failed to complete activity');
    } finally {
      setCompletingId(null);
      setExpandedActivity(null);
    }
  };

  return (
    <div className="pb-4">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/app')}
          className="w-10 h-10 rounded-full bg-white shadow flex items-center justify-center
                     hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <p className="text-sm text-gray-500">{week.month} - Week {week.number}</p>
          <h1 className="text-xl">{week.title}</h1>
        </div>
      </div>

      {/* Error Banner (non-blocking) */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-center gap-2"
        >
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-amber-700 text-sm">{error}</p>
        </motion.div>
      )}

      {/* Progress Card */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-5 mb-6 bg-gradient-to-br from-leaf-500 to-leaf-600 text-white"
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="text-leaf-100 text-sm">{week.theme}</p>
            <p className="text-white font-medium mt-1">
              {completedCount} of {totalCount} activities complete
            </p>
          </div>
          <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
            <Star size={16} className="text-sunshine-300" />
            <span className="font-semibold">{week.points} pts</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
            className="h-full bg-sunshine-400 rounded-full"
          />
        </div>
      </motion.div>

      {/* Description */}
      <div className="card p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info size={20} className="text-leaf-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-gray-700 text-sm">{week.description}</p>
            {week.plantCareFocus && (
              <p className="text-xs text-gray-500 mt-2">
                <span className="font-medium">Focus:</span> {week.plantCareFocus}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Environmental Day Banner */}
      {week.envDay && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card p-4 mb-6 bg-gradient-to-r from-sunshine-100 to-sunshine-200 border-2 border-sunshine-300"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-sunshine-400 flex items-center justify-center">
              <Gift size={24} className="text-white" />
            </div>
            <div>
              <p className="font-semibold text-sunshine-800">{week.envDay.name}</p>
              <p className="text-sm text-sunshine-700">
                Bonus: {week.envDay.bonus_points || week.envDay.points || 50} points!
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Activities List */}
      <h2 className="text-lg font-heading text-leaf-800 mb-4">Activities</h2>
      <div className="space-y-3">
        {activities.map((activity, index) => (
          <motion.div
            key={activity.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card overflow-hidden"
          >
            {/* Activity Header */}
            <button
              onClick={() => setExpandedActivity(
                expandedActivity === activity.id ? null : activity.id
              )}
              className="w-full p-4 flex items-start gap-4 text-left"
            >
              {/* Checkbox */}
              <div className="mt-0.5">
                {activity.completed ? (
                  <CheckCircle2 size={24} className="text-leaf-500" />
                ) : (
                  <Circle size={24} className="text-gray-300" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className={`font-semibold ${
                    activity.completed ? 'text-gray-500 line-through' : 'text-gray-800'
                  }`}>
                    {activity.title}
                  </h3>
                  <span className="badge badge-success text-xs">
                    +{activity.points} pts
                  </span>
                </div>
                <p className={`text-sm mt-1 ${
                  activity.completed ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {activity.description}
                </p>
              </div>

              {/* Expand Icon */}
              {expandedActivity === activity.id ? (
                <ChevronUp size={20} className="text-gray-400" />
              ) : (
                <ChevronDown size={20} className="text-gray-400" />
              )}
            </button>

            {/* Expanded Content */}
            <AnimatePresence>
              {expandedActivity === activity.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="border-t border-gray-100"
                >
                  <div className="p-4 bg-gray-50">
                    {/* Instructions */}
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                        Instructions
                      </p>
                      <p className="text-sm text-gray-700">{activity.instructions}</p>
                    </div>

                    {/* Tips */}
                    <div className="mb-4 p-3 bg-sunshine-50 rounded-lg">
                      <p className="text-xs font-semibold text-sunshine-700 uppercase mb-1">
                        Tip
                      </p>
                      <p className="text-sm text-sunshine-800">{activity.tips}</p>
                    </div>

                    {/* Complete Button */}
                    {!activity.completed && (
                      <button
                        onClick={() => handleComplete(activity.id)}
                        disabled={completingId === activity.id}
                        className="btn-primary w-full flex items-center justify-center gap-2"
                      >
                        {completingId === activity.id ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <CheckCircle2 size={20} />
                            Mark as Complete
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>

      {/* Complete Week Button */}
      {completedCount === totalCount && totalCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 text-center"
        >
          <p className="text-leaf-600 font-medium mb-4">
            All activities completed! Ready to unlock the next week?
          </p>
          <button
            onClick={() => navigate('/app')}
            className="btn-secondary inline-flex items-center gap-2"
          >
            <Sparkles size={20} />
            Back to Level Map
          </button>
        </motion.div>
      )}
    </div>
  );
}
